
import {finalize} from 'rxjs/operators';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    ChangeDetectorRef,
    ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import { AuthService } from '../../core/auth.service';
import { IMyDateModel, IMyDpOptions, IMyInputFieldChanged, IMyInputFocusBlur } from 'mydatepicker';
import { PatientsService } from '../patients.service';
import * as moment from 'moment';
import { transition } from '@angular/animations';
import {PopoverDirective} from 'ngx-bootstrap/popover';

@Component({
    selector: 'app-patients-modal',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './patients-modal.component.html',
    styleUrls: ['./patients-modal.component.scss']
})

export class PatientsModalComponent implements OnChanges {
    type: string;
    dpkBirthdayOptions: IMyDpOptions = {
        dateFormat: 'dd.mm.yyyy',
        showTodayBtn: false,
        disableSince: { year: null, month: null, day: null }
    };

    dpkEntryDateOptions: IMyDpOptions = {
        dateFormat: 'dd.mm.yyyy',
    };

    validateCssBirthday = 'form-unfocused';
    validateCssEntryDate = 'form-unfocused';
    isValidBirthday = true;
    isValidEntryDate = true;

    age: number;
    dateFormat = 'DD.MM.YYYY';

    @Input() params: Object;
    @Input() formLoading: boolean;
    @Input() patientForm: FormGroup;
    @Input() errorMessage: string[];
    @Input() successMessage: string;
    @Input() doctors: Object[];
    @Input() assistants: Object[];
    @Input() mpa: Object[];
    @Input() authService: AuthService;
    @Input() token: string;
    @Input() patient;
    // @Input() createSE;
    @Input() createSEout;

    @Output() hideModal = new EventEmitter<any>();
    @Output() submitPatient = new EventEmitter<any>();
    @Output() submitPatientWithSE = new EventEmitter<any>();
    @Output() activate = new EventEmitter<any>();
    @Output() delete = new EventEmitter<any>();
    @Input() languages: Object[];

    @ViewChild('revocationPop', { static: false }) revocationPop: PopoverDirective;
    revocationForm: FormGroup;
    revocationMessage: string;
    revocationOptions = [
        { value: 'Revocation was given verbally by the patient', key: 1 },
        { value: 'Revocation was given in writing by the patient', key: 2 }
    ];

    constructor(
        public router: Router,
        public patientsService: PatientsService,
        public cdr: ChangeDetectorRef
    ) {
    }

    ngOnChanges(change) {

        if (this.patientForm != null) {
            switch (this.patientForm.controls.type.value) {
                case 1:
                    this.type = 'Activated';
                    break;
                case 2:
                    this.type = 'Shared';
                    break;
                case 3:
                    this.type = 'Archived';
                    break;
            }

            if (this.patient && !change.successMessage) {
                this.patientForm.reset(this.patient);
                this.initCalendarSettings();
                this.setEntryDateForm(this.patient);
                this.setBirthdayForm(this.patient);
                this.initRevocationForm();

                this.patientForm.controls['language_id'].setValue(this.patient.language_id);
            }
        }

    }

    /**
     * Init Revocation form
     */
    initRevocationForm(): void {

        this.revocationMessage = '';

        const revocations = new FormArray(this.revocationOptions.map((r) => {
            return new FormGroup({
                key: new FormControl(r.key),
                value: new FormControl(r.value),
                checked: new FormControl(false)
            });
        }));

        this.revocationForm = new FormGroup({
            revocations: revocations,
            selectedRevocations: new FormControl(this.mapRevocations(revocations.value), Validators.required)
        });

        revocations.valueChanges.subscribe((v) => {
            this.revocationForm.controls.selectedRevocations.setValue(this.mapRevocations(v));
        });
    }

    /**
     * Reset Revocation form
     */
    resetRevocationForm(): void {
        this.revocationMessage = '';
        this.initRevocationForm();
    }

    /**
     * Init validation for calendar config
     */
    initCalendarSettings(): void {
        const tomorrowDate = new Date();
        tomorrowDate.setDate(tomorrowDate.getDate() + 1);

        this.dpkBirthdayOptions.disableSince.year = +tomorrowDate.getFullYear();
        this.dpkBirthdayOptions.disableSince.month = +tomorrowDate.getMonth() + 1;
        this.dpkBirthdayOptions.disableSince.day = +tomorrowDate.getDate();
    }

    /**
     * Init value for form control birthday
     *
     * @param patient
     */
    setBirthdayForm(patient): void {
        if (!patient.birthday) {
            return;
        }

        const birthday = moment(patient.birthday, this.dateFormat);

        this.age = Math.abs(birthday.diff(moment(), 'years'));

        this.patientForm.patchValue({
            birthday: {
                date: {
                    day: +birthday.format('D'),
                    month: +birthday.format('M'),
                    year: +birthday.format('Y')
                }
            }
        });
    }

    /**
     * Init value for form control entry date
     * @param patient
     */
    setEntryDateForm(patient): void {
        if (!patient.entry_date) {
            return;
        }

        const entryDate = moment(patient.entry_date, this.dateFormat);

        this.patientForm.patchValue({
            entry_date: {
                date: {
                    day: +entryDate.format('D'),
                    month: +entryDate.format('M'),
                    year: +entryDate.format('Y')
                }
            }
        });
    }

    /**
     * Handle user change birthday date => update age
     * @param {IMyDateModel} event
     */
    onBirthdayChanged(event: IMyDateModel) {
        if (!event.formatted) {
            this.age = 0;
            return;
        }

        const birthday = moment(event.formatted, this.dateFormat);
        this.age = Math.abs(birthday.diff(moment(), 'years'));
        this.isValidBirthday = true;
    }

    /**
     * Handle focus in and focus out input
     * @param {IMyInputFocusBlur} event
     */
    onBirthdayFocusBlur(event: IMyInputFocusBlur, formGroup: any): void {
        if (event.reason === 2 && event.value !== '' && !this.isValidBirthday) {
            this.validateCssBirthday = 'form-errored';
        } else if (event.reason === 2) {
            this.validateCssBirthday = 'form-unfocused';
        } else {
            this.validateCssBirthday = 'form-focused mat-focused';
        }
    }

    /**
     * Handle value of birthday change
     * @param {IMyInputFieldChanged} event
     */
    onBirthdayInputChanged(event: IMyInputFieldChanged) {
        if (event.value !== '') {
            this.isValidBirthday = event.valid;
        }
    }

    onEntryDateFocusBlur(event: IMyInputFocusBlur, formGroup): void {
        if (event.reason === 2 && !this.isValidEntryDate) {
            this.validateCssEntryDate = 'form-errored';
        } else if (event.reason === 2) {
            this.validateCssEntryDate = 'form-unfocused';
        } else {
            this.validateCssEntryDate = 'form-focused mat-focused';
        }
    }

    onEntryDateInputChanged(event: IMyInputFieldChanged) {
        this.isValidEntryDate = event.valid;
    }

    /**
     * Clear validation message on hide modal window
     */
    clearValidationMessages() {
        this.validateCssBirthday = '';
        this.validateCssEntryDate = '';
        this.isValidBirthday = true;
        this.isValidEntryDate = true;
    }

    onInputFocused(event, formGroup: any, isBottomField?: boolean) {
        let uagent = navigator.userAgent.toLowerCase();
        if (!(/safari/.test(uagent) && !/chrome/.test(uagent))) {
            // wait for the virtual keyboard to scroll up so that the scroll height can be updated correctly
            setTimeout(() => {
                if (isBottomField) {
                    document.body.scroll({
                        top: document.body.scrollHeight,
                        left: 0,
                        behavior: 'smooth'
                    })
                } else {
                    document.body.scroll({
                        top: 0,
                        left: 0,
                        behavior: 'smooth'
                    });
                }

                let modalEl = document.getElementsByClassName('patient-creation-modal')[0] as HTMLElement;
                modalEl.scroll({
                    top: formGroup.offsetTop,
                    left: 0,
                    behavior: 'smooth'
                });

            }, 200);
        }
    }

    onPatientAgreementRevoked() {
        if (!this.patient || !this.revocationForm.controls.selectedRevocations.valid) {
            return;
        }

        this.revocationMessage = '';

        this.patientsService.revokeOnPatientAgreement(this.patient.id, this.revocationForm.controls.selectedRevocations.value).pipe(
            finalize(() => {
                setTimeout(() => {
                    this.revocationPop.hide();
                    this.patient.is_agreement_exist = false;
                }, 2000);
            }))
            .subscribe(
                res => {
                    this.revocationMessage = res['message'];

                    this.cdr.markForCheck();
                },
                err => {
                    // will handle error later
                });
    }

    mapRevocations(revocations) {
        const selectedRevocations = revocations.filter((r) => r.checked).map((r) => r.key);
        return selectedRevocations.length ? selectedRevocations : null;
    }

}
